import { useCallback, useEffect, useRef, useState } from 'react';
import { analysisParameterError, fetchAnalysisRebuildStatus } from '../api/analysisParameters';
import type { AnalysisRebuildStatus } from '../types';

const POLL_INTERVAL_MS = 5000;
const POLL_LIMIT_MS = 15 * 60 * 1000;

const rebuildRunning = (status: AnalysisRebuildStatus | null) =>
  status?.status === 'queued' || status?.status === 'rebuilding';

export function newerRebuild(candidate: AnalysisRebuildStatus, previous: AnalysisRebuildStatus | null) {
  return candidate.requested_at !== null && (previous?.requested_at == null
    || Date.parse(candidate.requested_at) > Date.parse(previous.requested_at));
}

export function useAnalysisRebuildStatus(deviceId: number, analysisKey: string,
  onCompleted?: (status: AnalysisRebuildStatus) => Promise<AnalysisRebuildStatus | undefined>,
  onPollSuccess?: () => void) {
  const [status, setStatus] = useState<AnalysisRebuildStatus | null>(null);
  const [pollExpired, setPollExpired] = useState(false);
  const [error, setError] = useState('');
  const [cycle, setCycle] = useState(0);
  const version = useRef(0);
  const started = useRef<number | null>(null);
  const latest = useRef(status);
  const identity = `${deviceId}:${analysisKey}`;
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const completed = useRef(onCompleted);
  completed.current = onCompleted;
  const polled = useRef(onPollSuccess);
  polled.current = onPollSuccess;

  const reset = useCallback((next: AnalysisRebuildStatus | null) => {
    version.current += 1;
    latest.current = next;
    started.current = null;
    setStatus(next);
    setError('');
    setPollExpired(false);
    setCycle(value => value + 1);
  }, []);

  const update = useCallback((next: AnalysisRebuildStatus) => {
    if (latest.current && newerRebuild(latest.current, next)) return;
    if (newerRebuild(next, latest.current)) {
      started.current = null;
      setPollExpired(false);
    }
    latest.current = next;
    setStatus(next);
  }, []);

  const acceptCompletion = useCallback((finished: AnalysisRebuildStatus, refreshed?: AnalysisRebuildStatus) => {
    update(refreshed && newerRebuild(refreshed, finished) ? refreshed : finished);
  }, [update]);

  const polling = rebuildRunning(status);
  useEffect(() => {
    if (!polling || !analysisKey || pollExpired) return;
    const requestVersion = version.current;
    const pollStart = started.current ?? Date.now();
    started.current = pollStart;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const current = () => requestVersion === version.current && currentIdentity.current === identity;
    const poll = async () => {
      if (cancelled || !current()) return;
      if (Date.now() - pollStart >= POLL_LIMIT_MS) {
        setPollExpired(true);
        return;
      }
      try {
        const next = await fetchAnalysisRebuildStatus(analysisKey, deviceId);
        if (cancelled || !current()) return;
        setError('');
        polled.current?.();
        update(next);
        if (next.status === 'completed') {
          const refreshed = await completed.current?.(next);
          // Completion changes the polling effect; a separately guarded details
          // refresh can still discover that another rebuild has started.
          if (current()) acceptCompletion(next, refreshed);
          return;
        }
        if (!rebuildRunning(next)) return;
      } catch (failure: unknown) {
        if (!current()) return;
        setError(analysisParameterError(failure));
      }
      if (!cancelled && current()) timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timer = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [deviceId, analysisKey, identity, polling, pollExpired, status?.requested_at, cycle, update, acceptCompletion]);

  useEffect(() => () => { version.current += 1; }, [identity]);
  return { status, pollExpired, error, reset, update, acceptCompletion };
}
